import { UploadSimple } from "@phosphor-icons/react";
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  Flex,
  Table,
  Text,
} from "@radix-ui/themes";
import { useEffect, useState } from "react";

import { FileDropzone } from "../../../../../components/FileDropzone";
import { useUI } from "../../../../../contexts/ui";
import { uploadFile } from "../../../../../services/upload";
import { errorMessage } from "../../../../../utils/http";
import {
  useConfirmSpreadsheetImportMutation,
  useGetSpreadsheetImportJobQuery,
  useStartSpreadsheetImportMutation,
} from "../../../../../utils/queries/knowledge-db";
import { queryClient } from "../../../../../utils/queryClient";

type Step = "upload" | "processing" | "preview" | "importing" | "complete";

export function ImportSpreadsheetDialog({
  modelId,
  open,
  onOpenChange,
  onSuccess,
}: {
  modelId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void | Promise<void>;
}) {
  const { notify } = useUI();
  const [step, setStep] = useState<Step>("upload");
  const [jobId, setJobId] = useState<string | null>(null);
  const [applyProposedProperties, setApplyProposedProperties] = useState(true);
  const [uploading, setUploading] = useState(false);

  const startMutation = useStartSpreadsheetImportMutation();
  const confirmMutation = useConfirmSpreadsheetImportMutation();
  const jobQuery = useGetSpreadsheetImportJobQuery(
    modelId,
    jobId ?? undefined,
    step === "processing" || step === "importing",
  );

  useEffect(() => {
    if (!open) {
      setStep("upload");
      setJobId(null);
      setApplyProposedProperties(true);
      setUploading(false);
    }
  }, [open]);

  useEffect(() => {
    const job = jobQuery.data;
    if (!job) return;

    if (step === "processing") {
      if (job.status === "preview_ready") {
        setStep("preview");
      } else if (job.status === "failed") {
        setStep("upload");
        notify({
          variant: "error",
          title: "Import failed",
          message: job.error ?? "Could not parse spreadsheet",
        });
      }
    }

    if (step === "importing") {
      if (job.status === "complete") {
        setStep("complete");
        void onSuccess?.();
      } else if (job.status === "failed") {
        setStep("preview");
        notify({
          variant: "error",
          title: "Import failed",
          message: job.error ?? "Could not import records",
        });
      }
    }
  }, [jobQuery.data, notify, onSuccess, step]);

  useEffect(() => {
    if (step !== "importing" || !jobQuery.isError) return;

    setStep("preview");
    notify({
      variant: "error",
      title: "Import status unavailable",
      message: "Could not poll import progress. Try again or refresh the page.",
    });
  }, [jobQuery.isError, notify, step]);

  async function handleFilesSelected(files: File[]) {
    const file = files[0];
    if (!file) return;

    setStep("processing");
    setUploading(true);
    try {
      const uploaded = await uploadFile(file);
      const started = await startMutation.mutateAsync({
        modelId,
        payload: { s3Key: uploaded.key, fileName: file.name },
      });
      const jobIdFromStart = started?._id;
      if (!jobIdFromStart) {
        throw new Error("Import job did not return an id");
      }
      setJobId(jobIdFromStart);
    } catch (err) {
      setStep("upload");
      notify({
        variant: "error",
        title: "Upload failed",
        message: await errorMessage(err),
      });
    } finally {
      setUploading(false);
    }
  }

  async function handleConfirm() {
    if (!jobId) return;
    setStep("importing");
    try {
      await confirmMutation.mutateAsync({
        modelId,
        jobId,
        payload: { applyProposedProperties },
      });
      await queryClient.invalidateQueries({
        queryKey: ["spreadsheet-import", modelId, jobId],
      });
    } catch (err) {
      setStep("preview");
      notify({
        variant: "error",
        title: "Could not start import",
        message: await errorMessage(err),
      });
    }
  }

  const preview = jobQuery.data?.preview;
  const previewRows = preview?.rows?.slice(0, 20) ?? [];
  const previewColumns =
    previewRows.length > 0 ? Object.keys(previewRows[0] ?? {}) : [];

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        className="flex max-h-[85vh] flex-col gap-4"
        style={{ maxWidth: "48rem" }}
        data-testid="import-spreadsheet-dialog"
      >
        <Dialog.Title>Import spreadsheet</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Upload a spreadsheet to add approved claims to the Knowledge Database.
          Rows are normalized and deduplicated by claim text.
        </Dialog.Description>

        {step === "upload" && (
          <FileDropzone
            accept=".csv,.tsv,.xlsx,.xls"
            disabled={uploading || startMutation.isPending}
            onFiles={(files) => void handleFilesSelected(files)}
            data-testid="import-spreadsheet-dropzone"
            inputTestId="import-spreadsheet-dropzone-input"
          />
        )}

        {(step === "processing" || step === "importing") && (
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="2"
            className="py-8"
            data-testid="import-spreadsheet-processing"
          >
            <UploadSimple size={24} className="animate-pulse" aria-hidden />
            <Text size="2">
              {step === "processing"
                ? jobId
                  ? "Parsing and normalizing spreadsheet…"
                  : "Uploading spreadsheet…"
                : "Creating records…"}
            </Text>
            {jobQuery.data?.status && (
              <Text size="1" color="gray">
                Status: {jobQuery.data.status}
              </Text>
            )}
          </Flex>
        )}

        {step === "preview" && preview && (
          <Flex direction="column" gap="3" className="min-h-0 flex-1">
            <Flex gap="3" wrap="wrap">
              <Text size="2">
                <strong>{preview.totalRows}</strong> rows found
              </Text>
              <Text size="2">
                <strong>{preview.dedupeCount}</strong> duplicates will be
                skipped
              </Text>
              <Text size="2">
                <strong>{preview.totalRows - preview.dedupeCount}</strong> new
                records to import
              </Text>
            </Flex>

            {preview.proposedProperties.length > 0 && (
              <Flex direction="column" gap="2">
                <Text size="2" weight="medium">
                  Proposed new columns
                </Text>
                <Flex gap="2" wrap="wrap">
                  {preview.proposedProperties.map(
                    (prop: { name: string; type: string }) => (
                      <Badge key={prop.name} variant="soft">
                        {prop.name}
                      </Badge>
                    ),
                  )}
                </Flex>
                <Text as="label" size="2">
                  <Flex gap="2" align="center">
                    <Checkbox
                      checked={applyProposedProperties}
                      onCheckedChange={(checked) =>
                        setApplyProposedProperties(checked === true)
                      }
                      data-testid="import-spreadsheet-apply-properties"
                    />
                    Add proposed columns to the Claims record type
                  </Flex>
                </Text>
              </Flex>
            )}

            <div className="min-h-0 flex-1 overflow-auto rounded-md border border-puntt-neutral-gray-6">
              <Table.Root size="1">
                <Table.Header>
                  <Table.Row>
                    {previewColumns.map((col) => (
                      <Table.ColumnHeaderCell key={col}>
                        {col}
                      </Table.ColumnHeaderCell>
                    ))}
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {previewRows.map(
                    (row: Record<string, unknown>, idx: number) => (
                      <Table.Row key={idx}>
                        {previewColumns.map((col) => (
                          <Table.Cell key={col}>
                            {String(row[col] ?? "")}
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    ),
                  )}
                </Table.Body>
              </Table.Root>
            </div>
          </Flex>
        )}

        {step === "complete" && jobQuery.data?.result && (
          <Flex
            direction="column"
            gap="2"
            data-testid="import-spreadsheet-complete"
          >
            <Text size="2">
              Imported <strong>{jobQuery.data.result.createdCount}</strong>{" "}
              records. Skipped{" "}
              <strong>{jobQuery.data.result.skippedCount}</strong> duplicates.
            </Text>
          </Flex>
        )}

        <Flex gap="2" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              {step === "complete" ? "Done" : "Cancel"}
            </Button>
          </Dialog.Close>
          {step === "preview" && (
            <Button
              onClick={() => void handleConfirm()}
              disabled={confirmMutation.isPending}
              data-testid="import-spreadsheet-confirm"
            >
              Import records
            </Button>
          )}
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
